import dayjs from "dayjs";
// Explicit subset of nested characteristics that are editable in the frontend unit form.
const FRONTEND_UNIT_FORM_CHARACTERISTIC_FIELDS = [
  "warehouseLightingType",
  "clearHeight",
  "gradeLevelDoors",
  "fireSuppressionSystem",
  "dockHighDoors",
  "mainBreakerSize",
];

const SOURCE_DATE_VALUE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/;

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const isValidDateOnlyValue = (year, month, day) => {
  const parsedDate = new Date(Date.UTC(year, month - 1, day));

  return (
    parsedDate.getUTCFullYear() === year && parsedDate.getUTCMonth() === month - 1 && parsedDate.getUTCDate() === day
  );
};

const isInvalidSourceDateString = value => {
  if (typeof value !== "string") return false;

  const match = value.trim().match(SOURCE_DATE_VALUE_PATTERN);
  if (!match) return false;

  const [, yearValue, monthValue, dayValue] = match;
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);

  return !isValidDateOnlyValue(year, month, day);
};

const isValidSourceDateValue = value => {
  if (!value || (typeof value === "string" && value.trim() === "")) return false;
  if (isInvalidSourceDateString(value)) return false;

  return dayjs(value).isValid();
};

export const getSourceLeaseDateOnlyValue = value => {
  if (!value) return value;

  if (typeof value === "string") {
    const match = value.trim().match(SOURCE_DATE_VALUE_PATTERN);

    if (match) {
      const [, yearValue, monthValue, dayValue] = match;
      const year = Number(yearValue);
      const month = Number(monthValue);
      const day = Number(dayValue);

      if (isValidDateOnlyValue(year, month, day)) return `${yearValue}-${monthValue}-${dayValue}`;

      return value;
    }
  }

  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate.format("YYYY-MM-DD") : value;
};

export const formatSourceLeaseDate = (value, format = "YYYY-MM-DD") => {
  const dateOnlyValue = getSourceLeaseDateOnlyValue(value);

  if (!dateOnlyValue || isInvalidSourceDateString(dateOnlyValue)) return dateOnlyValue;

  const parsedDate = dayjs(dateOnlyValue);
  return parsedDate.isValid() ? parsedDate.format(format) : dateOnlyValue;
};

export const getUnitAvailabilityDateDetails = unit => {
  const availabilityDateFields = [
    { field: "alternateLeaseEndDate", value: unit?.alternateLeaseEndDate },
    { field: "leaseEndDate", value: unit?.leaseInfo?.leaseEndDate },
    { field: "leaseEndDate", value: unit?.leaseEndDate },
    { field: "vacantDate", value: unit?.leaseInfo?.vacantDate },
    { field: "vacantDate", value: unit?.vacantDate },
  ];

  return availabilityDateFields.find(({ value }) => isValidSourceDateValue(value)) || { field: null, value: null };
};

export const getSystemUnitAvailabilityDateDetails = unit => {
  const availabilityDateFields = [
    { field: "leaseEndDate", value: unit?.leaseInfo?.leaseEndDate },
    { field: "leaseEndDate", value: unit?.leaseEndDate },
    { field: "vacantDate", value: unit?.leaseInfo?.vacantDate },
    { field: "vacantDate", value: unit?.vacantDate },
  ];

  return availabilityDateFields.find(({ value }) => isValidSourceDateValue(value)) || { field: null, value: null };
};

export const getSystemUnitAvailabilityDate = unit => getSystemUnitAvailabilityDateDetails(unit).value;

export const getMergedUnitSystemAvailabilityDate = (unit, sourceUnits = []) => {
  if (!unit?.ghostUnitCode || !Array.isArray(unit.unitCode) || unit.unitCode.length === 0) return null;

  const constituentDates = unit.unitCode.map(({ value }) => {
    const sourceUnit = sourceUnits.find(({ unitId }) => unitId === value);
    const date = getSystemUnitAvailabilityDate(sourceUnit);

    return date ? getSourceLeaseDateOnlyValue(date) : null;
  });

  if (constituentDates.some(date => !date)) return null;

  const [sharedDate] = constituentDates;
  return constituentDates.every(date => date === sharedDate) ? sharedDate : null;
};

export const getUnitsMissingAlternateAvailabilityDate = (units = [], sourceUnits = []) =>
  units.reduce((missingIndexes, unit, index) => {
    const systemDate = unit?.ghostUnitCode
      ? getMergedUnitSystemAvailabilityDate(unit, sourceUnits)
      : getSystemUnitAvailabilityDate(unit);

    if (!systemDate && !isValidSourceDateValue(unit?.alternateLeaseEndDate)) missingIndexes.push(index);
    return missingIndexes;
  }, []);
export const getMarketingUnitNameSuggestion = unitCode => {
  if (typeof unitCode !== "string") return undefined;

  const separatorIndex = unitCode.indexOf("~");
  if (separatorIndex < 0) return undefined;

  const suffix = unitCode
    .slice(separatorIndex + 1)
    .trim()
    .replace(/^0+/, "");
  return suffix ? `Unit ${suffix}` : undefined;
};

export const getMarketingUnitNameFields = (unit, unitCode, secondaryLanguages = [], isEnabled = false) => {
  if (!isEnabled) return {};

  const currentName = unit?.marketingUnitName;
  const hasExistingName = typeof currentName === "string" && currentName.trim();
  const suggestion = hasExistingName ? undefined : getMarketingUnitNameSuggestion(unitCode);

  if (!suggestion) return { marketingUnitName: currentName, translations: unit?.translations };

  const translations = secondaryLanguages.reduce((updatedTranslations, lang) => {
    const translatedUnit = updatedTranslations[lang] || {};
    const translatedName = translatedUnit.marketingUnitName;
    const hasTranslatedName = typeof translatedName === "string" && translatedName.trim();

    return hasTranslatedName
      ? updatedTranslations
      : {
          ...updatedTranslations,
          [lang]: { ...translatedUnit, marketingUnitName: suggestion },
        };
  }, unit?.translations || {});

  return { marketingUnitName: suggestion, translations };
};

export const getSyncedSystemAvailabilityLeaseInfo = (currentLeaseInfo, sourceUnit, value) => {
  const sourceAvailabilityDateField = getSystemUnitAvailabilityDateDetails(sourceUnit).field || "leaseEndDate";
  const sourceAvailabilityDate = value ? getSourceLeaseDateOnlyValue(value) : null;

  return {
    ...currentLeaseInfo,
    leaseEndDate: sourceAvailabilityDateField === "leaseEndDate" ? sourceAvailabilityDate : null,
    vacantDate: sourceAvailabilityDateField === "vacantDate" ? sourceAvailabilityDate : null,
  };
};

export const hasSyncableUnitSourceDetails = unitDetail => {
  if (!unitDetail) return false;

  return Object.keys(unitDetail.characteristics || {}).length > 0 || Boolean(getSystemUnitAvailabilityDate(unitDetail));
};

export const hydrateUnitFormCharacteristics = (unit = {}, isPropertySearchRedesignEnabled = false) => {
  if (!isPropertySearchRedesignEnabled) return { ...unit };

  const characteristics = unit.characteristics || {};

  return FRONTEND_UNIT_FORM_CHARACTERISTIC_FIELDS.reduce(
    (hydratedUnit, field) =>
      !hasOwn(unit, field) && hasOwn(characteristics, field)
        ? { ...hydratedUnit, [field]: characteristics[field] }
        : hydratedUnit,
    { ...unit },
  );
};

export const mergeCurrentUnitFormDetails = (savedUnit, formUnit) => {
  if (!savedUnit || !formUnit || Object.keys(formUnit).length === 0) return savedUnit;

  return {
    ...savedUnit,
    ...formUnit,
    unitId: savedUnit.unitId,
    unitCode: savedUnit.unitCode,
  };
};

export const getCurrentUnitFormDetails = (form, index, savedUnit) =>
  mergeCurrentUnitFormDetails(savedUnit, form.getFieldValue(["unitInfo", index]));
